import { CANCEL, ATTRIBUTES } from 'Pages/GMS/ISR/ISRConstants';
import React, { useState } from 'react';
import searchIcon from "../../../../../../assests/img/search-icon.svg";
import { TAttribute, TFieldDataConfiguration } from '../model';
import AttributeListItemRB from './AttributeListItemRB';
import { ConfirmationDialog } from 'Pages/GMS/ISR/shared/components/Common/ConfirmationDialog';

type TCategoryImportProps = {
    id?: string;
    attributeData: any;
    filteredAttributeData: any;
    showAttributeMenu: boolean;
    setShowAttributeMenu: React.Dispatch<React.SetStateAction<boolean>>;
    selectedAttribute: { type: string; data: TAttribute[] } | null;
    setSelectedAttribute: React.Dispatch<React.SetStateAction<{ type: string; data: TAttribute[] } | null>>;
    selectedItems: TAttribute[];
    setSelectedItems: React.Dispatch<React.SetStateAction<TAttribute[]>>;
    fieldDataConfiguration: TFieldDataConfiguration;
    setFieldDataConfiguration: React.Dispatch<React.SetStateAction<TFieldDataConfiguration | {}>>;
}

const ProductAttributeImport = (
    {
        id,
        attributeData,
        showAttributeMenu,
        setShowAttributeMenu,
        fieldDataConfiguration,
        setFieldDataConfiguration
    }: TCategoryImportProps) => {
    const [searchInput, setSearchInput] = useState('');
    const [pendingAttribute, setPendingAttribute] = useState<TAttribute | null>(null);

    const hasConfirmedAttribute = !!fieldDataConfiguration?.attributeId;

    const toggleMenu = () => {
        setShowAttributeMenu(!showAttributeMenu);
    };

    const handleDoneClick = () => {
        setShowAttributeMenu(false);
    };

    const handleCategoryToggle = (attribute: TAttribute) => {
        if (hasConfirmedAttribute) return;
        setPendingAttribute(attribute);
    };

    const handleConfirm = () => {
        if (pendingAttribute) {
            const updatedFieldDataConfiguration = { ...fieldDataConfiguration };
            updatedFieldDataConfiguration.choices = [];
            updatedFieldDataConfiguration.attributeName = pendingAttribute?.name;
            updatedFieldDataConfiguration.attributeId = pendingAttribute?.id;
            setFieldDataConfiguration(updatedFieldDataConfiguration);
        }
        setPendingAttribute(null);
    };

    const handleCancelConfirm = () => {
        setPendingAttribute(null);
    };
    const overlayClasses = 'tw-absolute tw-inset-0 tw-bg-black tw-opacity-50 tw-z-40 tw-h-full tw-w-full';
    const menuClasses = showAttributeMenu ? 'tw-absolute tw-top-0 tw-right-0 tw-inset-y-0 tw-w-80 tw-bg-white tw-z-50 tw-flex tw-flex-col tw-transform tw-translate-x-0 tw-transition-transform tw-ease-in-out tw-duration-300' : ' tw-transform tw-translate-x-full tw-transition-transform tw-ease-in-out tw-duration-300';

    // Filter and sort the attributeData based on searchInput and item.name
    const filteredAttributeData = attributeData
        ?.filter((item: { name: string }) => item.name.toLowerCase().includes(searchInput.toLowerCase()))
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));

    return (
        <>
            <div id={id} className="tw-h-[calc(100vh-60px)] tw-fixed tw-top-0 tw-left-0 tw-w-screen tw-z-40 tw-mt-16">
                <div data-testid="components-productattributeimport-div-1" className={overlayClasses} onClick={toggleMenu} />
                <div className={menuClasses}>
                    <div className={`tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-3 tw-sticky tw-top-0 tw-bg-white tw-z-10`}>
                        <h2 className={`tw-text-black tw-font-semibold tw-text-sm`}>{ATTRIBUTES}</h2>
                        <button data-testid="components-productattributeimport-button-1" className={`tw-text-black`} onClick={toggleMenu}>X</button>
                    </div>
                    <div className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-2 tw-mx-3'>
                        <div className='tw-flex tw-flex-grow tw-items-center tw-h-[32px] tw-p-2 tw-gap-3 tw-rounded-[4px] tw-border-[1px] tw-border-[#7676801F] tw-bg-white'>
                            <img src={searchIcon} alt="Search Category" />
                            <input data-testid="components-productattributeimport-input-2"
                                id="modelSearch"
                                type="search"
                                placeholder={"Search"}
                                className='tw-w-full tw-text-[#747593] tw-font-normal focus:tw-outline-none tw-border-0 tw-text-xs tw-block tw-bg-transparent'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='tw-flex-1 tw-overflow-y-auto tw-mt-2 tw-mx-3'>
                        {filteredAttributeData && filteredAttributeData.map((item: TAttribute) => {
                            const isSelected = fieldDataConfiguration ? fieldDataConfiguration?.attributeId === item?.id : false;
                            const isDisabled = hasConfirmedAttribute && !isSelected;
                            return (
                                <AttributeListItemRB
                                    key={item.id}
                                    item={item}
                                    isSelected={isSelected}
                                    onToggle={() => handleCategoryToggle(item)}
                                    disabled={isDisabled}
                                />
                            );
                        })}
                    </div>
                    <div className='tw-flex tw-justify-end tw-px-3 tw-py-3 tw-border-t tw-border-[#D1D1D6] tw-bg-white'>
                        <button data-testid="components-productattributeimport-button-3" className='btn-outlined' onClick={toggleMenu}>{CANCEL}</button>
                        <button data-testid="components-productattributeimport-button-4" className='button-style' onClick={handleDoneClick}>Done</button>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                id="attribute-import-confirm-modal"
                isOpen={!!pendingAttribute}
                title="Import from Attribute"
                message={`Are you sure you want to import from "${pendingAttribute?.name}"?`}
                secondaryMessage="This selection cannot be changed later."
                confirmText="Confirm"
                cancelText="Cancel"
                onConfirm={handleConfirm}
                onCancel={handleCancelConfirm}
            />
        </>
    );
}

export default ProductAttributeImport;
